import { nanoid } from 'nanoid';
import React, { Component } from 'react';
import /*toast, */ { Toaster } from 'react-hot-toast';
import { Navigate } from 'react-router-dom';
import track from 'react-tracking';
import { GlobalContext } from '../../interfaces/GlobalContext/GlobalContext';
import { OrderDataFieldConfigurations, OrderDataFields } from '../../interfaces/OrderData/OrderData';
import ProductPrice from '../../interfaces/ProductPrice/ProductPrice';
import { FormFieldName } from '../../interfaces/ProductProperties/ProductProperties';
import Snapshot from '../../interfaces/Snapshot/Snapshot';
import TrackingMeta from '../../interfaces/Tracking/TrackingMeta';
import { TrackingVariantETracker } from '../../interfaces/Tracking/TrackingVariantETracker';
import { UrlPaths } from '../../interfaces/UrlPaths/UrlPaths';
import FormField from '../FormField/FormField';
import FormFieldChooseGroup from '../FormFieldChooseGroup/FormFieldChooseGroup';
import FormFieldHidden from '../FormFieldHidden/FormFieldHidden';
import ResultPrice from '../ResultPrice/ResultPrice';
import { orderDataFieldConfigurations } from './OrderDataFieldConfigurations';
import ResultPriceWithFormElementProps from './ResultPriceWithFormElement.props';
import ResultPriceWithFormElementState from './ResultPriceWithFormElement.state';

@track({ /*page: UrlPaths.CHECKDATAPAGE*/ })
export default class ResultPriceWithFormElement extends Component<ResultPriceWithFormElementProps, ResultPriceWithFormElementState> {
    static contextType = GlobalContext;
    context!: React.ContextType<typeof GlobalContext>;
    private formElement: React.RefObject<HTMLFormElement>;
    private formId: string;
    private modalElement: React.RefObject<HTMLDivElement>;
    private orderDataFieldConfigurations: OrderDataFieldConfigurations;
    calculateResultPrice: () => ProductPrice;

    constructor(props: ResultPriceWithFormElementProps) {
        super(props);
        this.modalElement = React.createRef<HTMLDivElement>();
        this.formElement = React.createRef<HTMLFormElement>();
        this.orderDataFieldConfigurations = orderDataFieldConfigurations;
        this.formId = nanoid();
        this.handleSendOrder = this.handleSendOrder.bind(this);
        this.handleBackClick = this.handleBackClick.bind(this);
        this.userHasPermissionToBuy = this.userHasPermissionToBuy.bind(this);
        this.formIsValid = this.formIsValid.bind(this);
        this.calculateResultPrice = ResultPrice.prototype.calculateResultPrice;

        this.state = {
            price: this.calculateResultPrice(),
            paymentPeriodInEachProductIsTheSame: false,
            this: this,
        };
    }

    updateAndNotify = () => {
        this.setState({
            price: this.calculateResultPrice()
        });
    }

    getSnapshotBeforeUpdate(prevProps: ResultPriceWithFormElementProps): Snapshot {
        return { notifyRequired: prevProps.prices !== this.props.prices };
    }

    componentDidUpdate(prevProps: ResultPriceWithFormElementProps, prevState: ResultPriceWithFormElementState, snapshot: Snapshot) {
        if (snapshot.notifyRequired) {
            this.updateAndNotify();
        }
    }

    componentDidMount() {
        this.userHasPermissionToBuy();
    }

    @track((props: ResultPriceWithFormElementProps, state: ResultPriceWithFormElementState) => ({
        trackingMeta: {
            variant: TrackingVariantETracker.SENDORDER
        } as TrackingMeta,
        orderNumber: nanoid(),
        status: 'sale',
        orderPrice: state.price.price.toString(),
        currency: 'EUR',
        basket: {
            id: nanoid(),
            products: props.orderData.products?.map((product) => {
                const productFromLS = state.this.context.localStorageProvider.getProductInformationFromLocalStorage(product.magicNumber);
                // max 4 items!!
                const categoryArray = new Array<string>();
                categoryArray.push(FormFieldName.PAYMENTPERIOD + product.paymentPeriod);
                categoryArray.push('shop');
                categoryArray.push('not_available');
                return {
                    product: {
                        id: product.magicNumber,
                        name: `${productFromLS?.productProperties?.queryInformation.title}`,
                        category: categoryArray,
                        price: `${props.prices[product.magicNumber]?.price || 0}`,
                        currency: 'EUR',
                    },
                    quantity: product.numberOfLicences || 1
                };
            }) || []
        },
        customerGroup: '', // props.orderData.customer?.branche,
        deliveryConditions: '',
        paymentConditions: `${props.orderData.customer?.payType}`
    }))
    trackHandleSendOrder() {
        return;
    }
    
    async handleSendOrder(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        e.stopPropagation();
        if (this.formIsValid() && await this.userHasPermissionToBuy()) {
            this.trackHandleSendOrder();
            const orderData = this.props.orderData;
            // MARTP-1092: Remove unnecassary field
            delete orderData.customer?.trackingConsent;

            // MARTP-1132: OrderData should send false as String not as Boolean
            // orderData.products?.forEach((orderDataProduct) => {
            //     for (const key in orderDataProduct.additionalParameters) {
            //         if (orderDataProduct.additionalParameters[key] === 'true') {
            //             orderDataProduct.additionalParameters[key] = true;
            //         } else if (orderDataProduct.additionalParameters[key] === 'false') {
            //             orderDataProduct.additionalParameters[key] = false;   
            //         }
            //     }
            // });

            for (const key in this.orderDataFieldConfigurations) {
                let fieldValue = this.orderDataFieldConfigurations[key as OrderDataFields].ref?.getValue();
                
                if (fieldValue === '' || (fieldValue === undefined && key === OrderDataFields.TRACKINGCONSENT)) {
                    fieldValue = 'false';
                }

                if (fieldValue !== undefined) {
                    orderData[key as OrderDataFields] = fieldValue as string;
                }
            }
            
            const response = await this.context.requestProvider.postData('/order-api/sendOrder', orderData);
            if (response && (response.status === 200 || response.status === 201)) {
                this.setState({
                    navigate: UrlPaths.ENDPAGE
                }, () => {
                    const cartData = this.context.localStorageProvider.getProductInformationFromLocalStorage()?.warenkorbData || [];
                    const customerData = this.context.localStorageProvider.getCustomerDataFromLocalStorage();
                    this.context.sessionStorageProvider.setProductInformationInSessionStorage(cartData);
                    this.context.sessionStorageProvider.setCustomerDataInSessionStorage(customerData);
                    this.context.localStorageProvider.clearCustomerDataInLocalStorage();
                    this.context.localStorageProvider.clearProductInformationInLocalStorage();
                    this.context.localStorageProvider.removeCaptchaTokenFromLocalStorage();

                    this.forceUpdate();
                });
            } else if (response && response.status === 400) {
                this.setState({
                    modal: this.context.i18nProvider.get('friendlyCaptcha.captchaTokenInvalid') as string,
                }, () => {
                    this.modalElement.current?.classList.add('modal--visible');
                });
            } else {
                // toast.error( this.context.i18nProvider.get('checkDataPage.errorMessages.toastError') as string, {
                //     position: 'bottom-right'
                // });
                this.setState({
                    modal: this.context.i18nProvider.get('checkDataPage.errorMessages.toastError') as string,
                }, () => {
                    this.modalElement.current?.classList.add('modal--visible');
                });
            }
        }
    }

    formIsValid(): boolean {
        let formIsValid = true;
        for (const key in this.orderDataFieldConfigurations) {
            const fieldIsValid = this.orderDataFieldConfigurations[key as OrderDataFields].ref?.isValid();
            if (fieldIsValid === false) {
                formIsValid = false;
                this.orderDataFieldConfigurations[key as OrderDataFields].ref?.toggleValidationError(true);
            } else {
                this.orderDataFieldConfigurations[key as OrderDataFields].ref?.toggleValidationError(false);
            }
        }

        return formIsValid;
    }

    async userHasPermissionToBuy(): Promise<boolean> {
        const userInformation = await this.context.loginProvider.getUserInformation();

        if (userInformation === 403) {
            this.modalElement.current?.classList.add('modal--visible');
            this.setState({
                modal: this.context.i18nProvider.get('customerDataPage.loginSection.noOrderPossible') as string,
                userPermission: false,
            })
        } else if (userInformation === 500) {
            this.modalElement.current?.classList.add('modal--visible');
            this.setState({
                modal: this.context.i18nProvider.get('customerDataPage.loginSection.serverError') as string,
            })
        } else if (userInformation === 401) {
            this.modalElement.current?.classList.add('modal--visible');
            this.setState({
                modal: this.context.i18nProvider.get('customerDataPage.loginSection.tokenExpired') as string,
            })
        } else {
            this.setState({
                userPermission: true,
            });
            return true;
        }

        return false;
    }

    handleBackClick() {
        this.modalElement.current?.classList.remove('modal--visible');
    }

    render(): React.ReactNode {
        if (this.state.navigate) {
            return <Navigate to={this.state.navigate} />
        }
        
        if (this.props.orderData.customer?.contactNo !== undefined && !this.context.loginProvider.hasJWTCookie()) {
            // this.setState({
            //     navigate: UrlPaths.CUSTOMERDATAPAGE,
            // }, this.forceUpdate);
            // return false;
            return <Navigate to={UrlPaths.CUSTOMERDATAPAGE} />
        }

        return (
            <>
                <Toaster />
                <div className="result-price-with-form-element">
                    <section className="information-textblock">
                        <div className="information-textblock__header">
                            <div className="information-textblock__header-title">{ this.context.i18nProvider.get('checkDataPage.termsAndCondition.headline') }</div>
                            <div className="information-textblock__header-hint">{ this.context.i18nProvider.get('checkDataPage.termsAndCondition.hint') }</div>
                        </div>
                        <div className="information-textblock__text">
                            <div className="information-textblock__text-content information-textblock__text-content-information" dangerouslySetInnerHTML={{ __html: this.context.i18nProvider.get('checkDataPage.termsAndCondition.textInformation') || "" }}>
                            </div>
                            {!this.context.loginProvider.hasJWTCookie() 
                                || (
                                    this.context.loginProvider.hasJWTCookie() 
                                    && this.props.orderData.customer?.trackingConsent !== 'true'
                                )
                                ? <div className="information-textblock__text-content information-textblock__text-acceptance" dangerouslySetInnerHTML={{ __html: this.context.i18nProvider.get('checkDataPage.termsAndCondition.textAcceptance') || "" }}></div>
                                : null
                            }
                            <div className="information-textblock__text-content information-textblock__text-privacy" dangerouslySetInnerHTML={{ __html: this.context.i18nProvider.get('checkDataPage.termsAndCondition.textInformationPrivacy') || "" }}>
                            </div>
                        </div>
                        <form className="form information-textblock__checkboxes" 
                            onSubmit={this.handleSendOrder} 
                            ref={this.formElement} 
                            id={this.formId}
                            noValidate={true}>
                            <fieldset className="form__fieldset">
                                <div className="form__fieldset-content">
                                    <fieldset className="form__group">
                                        <div className="form__row row">
                                            {
                                                !this.context.loginProvider.hasJWTCookie() 
                                                    || (
                                                        this.context.loginProvider.hasJWTCookie() 
                                                        && this.props.orderData.customer?.trackingConsent !== 'true'
                                                    )
                                                    ? <FormField>
                                                        <FormFieldChooseGroup id={OrderDataFields.TRACKINGCONSENT}
                                                            options={[
                                                                {
                                                                    label: this.context.i18nProvider.get('checkDataPage.termsAndCondition.checkboxes.privacy'),
                                                                    value: 'true'
                                                                }
                                                            ]}
                                                            required={false}
                                                            ref={instance => instance ? this.orderDataFieldConfigurations[OrderDataFields.TRACKINGCONSENT].ref = instance : undefined}
                                                            optionStyle={'checkbox'} />
                                                    </FormField>
                                                    : <FormFieldHidden 
                                                        id={OrderDataFields.TRACKINGCONSENT}
                                                        key={OrderDataFields.TRACKINGCONSENT}
                                                        ref={instance => instance ? this.orderDataFieldConfigurations[OrderDataFields.TRACKINGCONSENT].ref = instance : undefined}
                                                        value={'true'}
                                                    />
                                            }
                                            <FormField errorMessage={this.context.i18nProvider.get('checkDataPage.errorMessages.termsAndConditionsError') as string} >
                                                <FormFieldChooseGroup id={OrderDataFields.AGB}
                                                    options={[
                                                        {
                                                            label: this.context.i18nProvider.get('checkDataPage.termsAndCondition.checkboxes.agreeTermsAndConditions'),
                                                            value: 'true'
                                                        }
                                                    ]}
                                                    required={true}
                                                    ref={instance => instance ? this.orderDataFieldConfigurations[OrderDataFields.AGB].ref = instance : undefined}
                                                    optionStyle='checkbox' />
                                            </FormField>
                                        </div>
                                    </fieldset>
                                </div>
                            </fieldset>
                        </form>
                    </section>
                    <div className="result-price">
                        <div className="container result-price__container">
                            {
                                this.state.paymentPeriodInEachProductIsTheSame
                                    ? <>
                                        <div className="result-price__text-wrapper">
                                            <span className="result-price__price-text">{this.context.i18nProvider.get ? this.context.i18nProvider.get('cartPage.totalPriceComponent.title') : null}</span>
                                            <span className="result-price__price-info">{this.context.i18nProvider.get ? this.context.i18nProvider.get('cartPage.totalPriceComponent.priceInfo') : null}</span>
                                        </div>
                                        <div className="result-price__price-wrapper">
                                            <span className="result-price__price">{this.state.price.price.toLocaleString('de-DE', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                                useGrouping: true
                                            })}</span>
                                            <span className="result-price__price-label">{
                                                this.context.i18nProvider.get('generalRessources.currency')
                                            } {
                                                Object.keys(this.props.prices).length > 0 
                                                    ? (this.context.i18nProvider.get('generalRessources.priceSuffix') as string)
                                                        .replace(
                                                            '{{paymentperiod}}', 
                                                            this.context.i18nProvider.get(`productPropertiesOverlay.${
                                                                FormFieldName.PAYMENTPERIOD
                                                            }.periods.${
                                                                (this.state.price && this.state.price.paymentPeriod) || '1M'
                                                            }`) || ''
                                                        )
                                                    : null
                                            }</span>
                                        </div>

                                        <span className="result-price__price-includes"
                                            dangerouslySetInnerHTML={{
                                                __html: Object.keys(this.props.prices).length > 0
                                                    ? (this.context.i18nProvider.get('cartPage.totalPriceComponent.totalPriceTaxReference') as string)
                                                        .replace('{{priceinclmwst}}', (this.state.price.priceInclMwst).toLocaleString('de-DE', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                            useGrouping: true
                                                        }))
                                                        .replace(
                                                            '{{paymentperiod}}', 
                                                            this.context.i18nProvider.get(`productPropertiesOverlay.${
                                                                FormFieldName.PAYMENTPERIOD
                                                            }.periods.${
                                                                (this.state.price && this.state.price.paymentPeriod) || '1M'
                                                            }`) || ''
                                                        )
                                                    : ''
                                            }}>
                                        </span>
                                    </>
                                    : undefined
                            }
                            <button className="cta-button cta-button--button-red"
                                type="submit"
                                form={this.formElement.current?.id}>{
                                    this.context.utilityProvider.orderHasBuyableProduct(this.context.localStorageProvider.getProductInformationFromLocalStorage()?.warenkorbData || [])
                                        ? this.context.i18nProvider.get('checkDataPage.buttonLabel')
                                        : this.context.i18nProvider.get('checkDataPage.buttonLabelForTestOrder')
                                }</button>
                        </div>
                    </div>
                    <div className="result-price__modal">
                        <div className="modal modal__result-price" ref={this.modalElement}>
                            <div className="modal__container container">
                                <div className="modal__content">
                                    <button className="modal__close-btn" type="button" onClick={this.handleBackClick}>
                                        <span className="modal__close-btn-icon"></span>
                                    </button>
                                    <div className="modal__body">
                                        <div className="textblock" dangerouslySetInnerHTML={{
                                            __html: this.state.modal || 'help'
                                        }}></div>
                                        <br/>
                                        <div className="result-price__back-button-wrapper">
                                            <button className="text-icon-link text-icon-link--button text-icon-link--back" 
                                                type='button' 
                                                onClick={this.handleBackClick}>
                                                <span className="text-icon-link__icon"></span>
                                                <span className="text-icon-link__text">{this.context.i18nProvider.get('cartPage.totalPriceComponent.buttons.back')}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}
